import { useState } from 'react';
import { useAuth } from '../lib/auth.js';
import s from './DeleteDocumentButton.module.scss';

interface Props {
  id: string;
  filename: string;
  onDeleted: () => void;
}

// Owner-only (T-213): renders nothing for a plain admin, and the API enforces
// the same gate. Two-step inline confirm, matching the resource Danger zone.
export function DeleteDocumentButton({ id, filename, onDeleted }: Props) {
  const { admin } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!admin?.is_owner) return null;

  async function remove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/documents/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.status === 409) {
        setError('A signed contract can’t be deleted.');
        setConfirming(false);
        return;
      }
      // 404 = already gone (another tab, a double click): the list is stale, refresh it.
      if (!res.ok && res.status !== 404) throw new Error(String(res.status));
      onDeleted();
    } catch {
      setError('Could not delete this document. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className={s.wrap}>
      {!confirming ? (
        <button
          type="button"
          className={s.danger}
          aria-label={`Delete ${filename}`}
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
        >
          Delete
        </button>
      ) : (
        <>
          <span className={s.ask}>Permanently delete?</span>
          <button type="button" className={s.danger} onClick={() => void remove()} disabled={busy}>
            {busy ? 'Deleting…' : 'Yes, delete'}
          </button>
          <button
            type="button"
            className={s.plain}
            onClick={() => setConfirming(false)}
            disabled={busy}
          >
            Cancel
          </button>
        </>
      )}
      {error && (
        <span className={s.error} role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
